import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import {
  createDafAccount,
  createDonation,
  deleteDafAccount,
  deleteDonation,
  listDafAccounts,
  listDonations,
} from "../lib/api-modules/donations.js";
import {
  DAF_PROVIDER_LABELS,
  DONATION_PAYMENT_METHOD_LABELS,
  type CreateDafAccountInput,
  type CreateDonationInput,
  type DafProvider,
  type Donation,
  type DonationPaymentMethod,
  type MembershipRole,
} from "../lib/api-modules/types.js";
import { formatCentsCurrency } from "../lib/util/money.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Toast, useToast } from "../components/toast.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";

const CHARITY_PROMPTS = [
  "How much have we donated this year?",
  "What's my charitable deduction estimate?",
  "Should I donate cash or appreciated stock?",
  "How much is in my DAF accounts?",
  "What's on Form 8283 for my stock donations?",
  "What tax did I avoid by donating stock instead of selling it?",
];

/**
 * Charity tracking page. Phase 12.
 *
 * Three tabs:
 *   - Donations: ledger of charitable gifts (cash + check + ACH + stock + DAF + QCD)
 *   - DAFs: donor-advised-fund accounts (manual entry; integration deferred Phase 14+)
 *   - Tax-year rollup: per-year totals + tax-saved estimate
 */

const READ_ONLY: ReadonlySet<MembershipRole> = new Set(["viewer", "auditor"]);

type Tab = "donations" | "dafs" | "rollup";

const todayIso = () => new Date().toISOString().slice(0, 10);

export function CharityPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>("donations");
  if (!user) return null;
  const canWrite = !READ_ONLY.has(user.role);

  return (
    <div className="mx-auto max-w-5xl p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Charity</h1>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
          Charitable donations + DAF accounts. Stock donations carry
          cost-basis fields for Form 8283 reporting (noncash &gt; $500).
        </p>
      </header>

      {!canWrite && (
        <p className="mb-4 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
          Read-only access — ask an owner or admin to make changes.
        </p>
      )}

      <div
        role="tablist"
        aria-label="Charity tabs"
        className="mb-6 flex gap-1 border-b border-zinc-200 dark:border-zinc-800"
      >
        {(
          [
            ["donations", "Donations"],
            ["dafs", "DAF accounts"],
            ["rollup", "Tax-year rollup"],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            onClick={() => setTab(k)}
            className={`-mb-px px-4 py-2 text-sm font-medium transition ${
              tab === k
                ? "border-b-2 border-brand-500 text-brand-700 dark:text-brand-300"
                : "text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "donations" && <DonationsTab canWrite={canWrite} />}
      {tab === "dafs" && <DafsTab canWrite={canWrite} />}
      {tab === "rollup" && <RollupTab />}

      <div className="mt-8">
        <SuggestedPromptsTile prompts={CHARITY_PROMPTS} />
      </div>
    </div>
  );
}

function DonationsTab({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const q = useQuery({
    queryKey: ["donations"],
    queryFn: () => listDonations({}),
  });
  const deleteM = useMutation({
    mutationFn: ({ date, id }: { date: string; id: string }) =>
      deleteDonation(date, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["donations"] });
      toast.show("Donation deleted");
    },
    onError: (e) => toast.show((e).message),
  });

  if (q.isLoading) return <p className="text-sm text-zinc-500">Loading…</p>;
  if (q.error)
    return (
      <p className="text-sm text-red-600 dark:text-red-400">
        {(q.error).message}
      </p>
    );

  const donations = q.data?.donations ?? [];
  const ytdTotal = q.data?.ytd_total_cents ?? 0;

  return (
    <section className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat label="Total" value={formatCentsCurrency(ytdTotal)} />
        <Stat label="Donations" value={String(donations.length)} />
        <Stat
          label="Tax years"
          value={String(q.data?.by_tax_year.length ?? 0)}
        />
      </div>
      <div className="flex justify-end">
        {canWrite && !showAdd && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600"
          >
            Add donation
          </button>
        )}
      </div>
      {showAdd && (
        <DonationForm onClose={() => setShowAdd(false)} />
      )}
      {donations.length === 0 ? (
        <p className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700">
          No donations recorded yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {[...donations]
            .sort((a, b) => (a.date < b.date ? 1 : -1))
            .map((d) => (
              <DonationCard
                key={d.donation_id}
                donation={d}
                canWrite={canWrite}
                onDelete={() =>
                  deleteM.mutate({ date: d.date, id: d.donation_id })
                }
                isMutating={deleteM.isPending}
              />
            ))}
        </ul>
      )}
      <Toast message={toast.message} />
    </section>
  );
}

function DonationCard({
  donation,
  canWrite,
  onDelete,
  isMutating,
}: {
  donation: Donation;
  canWrite: boolean;
  onDelete: () => void;
  isMutating: boolean;
}) {
  const isStock = donation.payment_method === "stock";
  const apprec =
    isStock &&
    donation.fmv_at_donation_cents !== undefined &&
    donation.cost_basis_cents !== undefined
      ? donation.fmv_at_donation_cents - donation.cost_basis_cents
      : 0;
  return (
    <li className="rounded-md border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-base font-medium">
              {donation.charity_name}
            </h3>
            <span className="rounded-full bg-zinc-200 px-2 py-0.5 text-[10px] font-normal text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
              {DONATION_PAYMENT_METHOD_LABELS[donation.payment_method]}
            </span>
            <span className="rounded-full bg-zinc-200 px-2 py-0.5 text-[10px] font-normal text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
              {donation.tax_year}
            </span>
          </div>
          <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
            <span className="font-medium text-zinc-700 dark:text-zinc-300">
              {formatCentsCurrency(donation.amount_cents)}
            </span>{" "}
            · <time dateTime={donation.date}>{donation.date}</time>
            {donation.charity_ein && <> · EIN {donation.charity_ein}</>}
          </p>
          {isStock && (
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
              {donation.quantity} × {donation.ticker}
              {donation.cost_basis_cents !== undefined && (
                <>
                  {" · basis "}
                  {formatCentsCurrency(donation.cost_basis_cents)}
                </>
              )}
              {apprec > 0 && (
                <>
                  {" · appreciation "}
                  <span className="font-medium text-emerald-700 dark:text-emerald-400">
                    {formatCentsCurrency(apprec)}
                  </span>
                </>
              )}
              {donation.acquired_date && (
                <> · acquired {donation.acquired_date}</>
              )}
            </p>
          )}
          {donation.notes && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-700 dark:text-zinc-300">
              {donation.notes}
            </p>
          )}
        </div>
        {canWrite && (
          <ConfirmButton
            label="Delete"
            pendingLabel="Deleting…"
            size="xs"
            ariaLabel={`Delete donation to ${donation.charity_name}`}
            onConfirm={onDelete}
            isPending={isMutating}
          />
        )}
      </div>
    </li>
  );
}

function DonationForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [date, setDate] = useState(todayIso());
  const [charityName, setCharityName] = useState("");
  const [charityEin, setCharityEin] = useState("");
  const [amountDollars, setAmountDollars] = useState("");
  const [paymentMethod, setPaymentMethod] =
    useState<DonationPaymentMethod>("cash");
  const [taxYear, setTaxYear] = useState(new Date().getUTCFullYear());
  const [notes, setNotes] = useState("");
  // Stock donation fields
  const [ticker, setTicker] = useState("");
  const [quantity, setQuantity] = useState("");
  const [costBasisDollars, setCostBasisDollars] = useState("");
  const [fmvDollars, setFmvDollars] = useState("");
  const [acquiredDate, setAcquiredDate] = useState("");

  const m = useMutation({
    mutationFn: (input: CreateDonationInput) => createDonation(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["donations"] });
      onClose();
    },
    onError: (e) => toast.show((e).message),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const dollarsToCents = (s: string) =>
      s.trim() ? Math.round(Number(s) * 100) : undefined;
    const amount = dollarsToCents(amountDollars);
    if (amount === undefined) {
      toast.show("Amount required");
      return;
    }
    const payload: CreateDonationInput = {
      date,
      charity_name: charityName,
      amount_cents: amount,
      payment_method: paymentMethod,
      tax_year: taxYear,
    };
    if (charityEin) payload.charity_ein = charityEin;
    if (notes) payload.notes = notes;
    if (paymentMethod === "stock") {
      payload.ticker = ticker;
      const q = Number(quantity);
      if (Number.isFinite(q) && q > 0) payload.quantity = q;
      const cb = dollarsToCents(costBasisDollars);
      if (cb !== undefined) payload.cost_basis_cents = cb;
      const fmv = dollarsToCents(fmvDollars);
      if (fmv !== undefined) payload.fmv_at_donation_cents = fmv;
      if (acquiredDate) payload.acquired_date = acquiredDate;
    }
    m.mutate(payload);
  };

  const isStock = paymentMethod === "stock";

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-3 rounded-md border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950"
    >
      <h3 className="text-sm font-semibold">Add donation</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Date">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className={inp}
          />
        </Field>
        <Field label="Charity name">
          <input
            type="text"
            value={charityName}
            onChange={(e) => setCharityName(e.target.value)}
            required
            className={inp}
            placeholder="e.g. American Red Cross"
          />
        </Field>
        <Field label="Charity EIN (optional)">
          <input
            type="text"
            value={charityEin}
            onChange={(e) => setCharityEin(e.target.value)}
            className={inp}
            placeholder="53-0196605"
          />
        </Field>
        <Field label="Tax year">
          <input
            type="number"
            value={taxYear}
            onChange={(e) => setTaxYear(Number(e.target.value))}
            min={2000}
            max={2100}
            required
            className={inp}
          />
        </Field>
        <Field label="Payment method">
          <select
            value={paymentMethod}
            onChange={(e) =>
              setPaymentMethod(e.target.value as DonationPaymentMethod)
            }
            className={inp}
          >
            {(
              Object.keys(
                DONATION_PAYMENT_METHOD_LABELS,
              ) as DonationPaymentMethod[]
            ).map((p) => (
              <option key={p} value={p}>
                {DONATION_PAYMENT_METHOD_LABELS[p]}
              </option>
            ))}
          </select>
        </Field>
        <Field label={isStock ? "FMV at donation ($)" : "Amount ($)"}>
          <input
            type="number"
            step="0.01"
            inputMode="decimal"
            value={amountDollars}
            onChange={(e) => {
              setAmountDollars(e.target.value);
              if (isStock) setFmvDollars(e.target.value);
            }}
            required
            className={inp}
          />
        </Field>
      </div>

      {isStock && (
        <>
          <h4 className="mt-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Stock donation (Form 8283 line items)
          </h4>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Ticker">
              <input
                type="text"
                value={ticker}
                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                required
                className={inp}
                placeholder="VTI"
              />
            </Field>
            <Field label="Quantity (shares)">
              <input
                type="number"
                step="0.001"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                required
                className={inp}
              />
            </Field>
            <Field label="Cost basis ($)">
              <input
                type="number"
                step="0.01"
                value={costBasisDollars}
                onChange={(e) => setCostBasisDollars(e.target.value)}
                required
                className={inp}
              />
            </Field>
            <Field label="Acquired date (Form 8283 line 2b)">
              <input
                type="date"
                value={acquiredDate}
                onChange={(e) => setAcquiredDate(e.target.value)}
                required
                className={inp}
              />
            </Field>
          </div>
        </>
      )}

      <Field label="Notes (optional)">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className={inp}
        />
      </Field>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={m.isPending}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-brand-600 disabled:opacity-60"
        >
          Save
        </button>
      </div>
      <Toast message={toast.message} />
    </form>
  );
}

function DafsTab({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const q = useQuery({
    queryKey: ["daf-accounts"],
    queryFn: listDafAccounts,
  });
  const deleteM = useMutation({
    mutationFn: deleteDafAccount,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["daf-accounts"] });
      toast.show("DAF deleted");
    },
    onError: (e) => toast.show((e).message),
  });

  if (q.isLoading) return <p className="text-sm text-zinc-500">Loading…</p>;

  const dafs = q.data?.daf_accounts ?? [];

  return (
    <section className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Stat
          label="Total DAF balance"
          value={formatCentsCurrency(q.data?.total_balance_cents ?? 0)}
        />
        <Stat label="DAF accounts" value={String(dafs.length)} />
      </div>
      <div className="flex justify-end">
        {canWrite && !showAdd && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600"
          >
            Add DAF
          </button>
        )}
      </div>
      {showAdd && <DafForm onClose={() => setShowAdd(false)} />}
      {dafs.length === 0 ? (
        <p className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700">
          No DAF accounts tracked yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {dafs.map((d) => (
            <li
              key={d.daf_account_id}
              className="rounded-md border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-medium">{d.fund_name}</h3>
                  <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                    {DAF_PROVIDER_LABELS[d.provider]}
                    {d.account_number_last4 && (
                      <> · ····{d.account_number_last4}</>
                    )}{" "}
                    ·{" "}
                    <span className="font-medium text-zinc-700 dark:text-zinc-300">
                      {formatCentsCurrency(d.balance_cents)}
                    </span>
                  </p>
                </div>
                {canWrite && (
                  <ConfirmButton
                    label="Delete"
                    pendingLabel="Deleting…"
                    size="xs"
                    ariaLabel={`Delete ${d.fund_name}`}
                    onConfirm={() => deleteM.mutate(d.daf_account_id)}
                    isPending={deleteM.isPending}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <Toast message={toast.message} />
    </section>
  );
}

function DafForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [provider, setProvider] = useState<DafProvider>("fidelity_charitable");
  const [fundName, setFundName] = useState("");
  const [last4, setLast4] = useState("");
  const [balanceDollars, setBalanceDollars] = useState("");
  const [notes, setNotes] = useState("");

  const m = useMutation({
    mutationFn: (input: CreateDafAccountInput) => createDafAccount(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["daf-accounts"] });
      onClose();
    },
    onError: (e) => toast.show((e).message),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const balance = balanceDollars.trim()
      ? Math.round(Number(balanceDollars) * 100)
      : 0;
    const payload: CreateDafAccountInput = {
      provider,
      fund_name: fundName,
      balance_cents: balance,
    };
    if (last4) payload.account_number_last4 = last4;
    if (notes) payload.notes = notes;
    m.mutate(payload);
  };

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-3 rounded-md border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950"
    >
      <h3 className="text-sm font-semibold">Add DAF</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Provider">
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value as DafProvider)}
            className={inp}
          >
            {(Object.keys(DAF_PROVIDER_LABELS) as DafProvider[]).map((p) => (
              <option key={p} value={p}>
                {DAF_PROVIDER_LABELS[p]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Fund name">
          <input
            type="text"
            value={fundName}
            onChange={(e) => setFundName(e.target.value)}
            required
            className={inp}
          />
        </Field>
        <Field label="Account # last 4">
          <input
            type="text"
            value={last4}
            onChange={(e) => setLast4(e.target.value)}
            pattern="\d{4}"
            className={inp}
          />
        </Field>
        <Field label="Balance ($)">
          <input
            type="number"
            step="0.01"
            value={balanceDollars}
            onChange={(e) => setBalanceDollars(e.target.value)}
            required
            className={inp}
          />
        </Field>
      </div>
      <Field label="Notes (optional)">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className={inp}
        />
      </Field>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={m.isPending}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-brand-600 disabled:opacity-60"
        >
          Save
        </button>
      </div>
      <Toast message={toast.message} />
    </form>
  );
}

function RollupTab() {
  const q = useQuery({
    queryKey: ["donations"],
    queryFn: () => listDonations({}),
  });
  const total = q.data?.ytd_total_cents ?? 0;
  const byTaxYear = q.data?.by_tax_year ?? [];
  // Tax-saved estimate at 24% marginal rate (rough — the AI tool will
  // reconcile against fire_assumptions.filing_status when available).
  const estTaxSaved = useMemo(() => Math.round(total * 0.24), [total]);

  if (q.isLoading) return <p className="text-sm text-zinc-500">Loading…</p>;

  return (
    <section className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Stat label="Total donated" value={formatCentsCurrency(total)} />
        <Stat
          label="Estimated tax saved (24% marginal)"
          value={formatCentsCurrency(estTaxSaved)}
        />
      </div>
      {byTaxYear.length === 0 ? (
        <p className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700">
          No donations recorded yet.
        </p>
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-left text-xs uppercase text-zinc-500 dark:border-zinc-800">
              <th className="py-2">Tax year</th>
              <th className="py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {byTaxYear.map((r) => (
              <tr
                key={r.tax_year}
                className="border-b border-zinc-100 dark:border-zinc-900"
              >
                <td className="py-2">{r.tax_year}</td>
                <td className="py-2 text-right font-medium">
                  {formatCentsCurrency(r.total_cents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

const inp =
  "w-full rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-950";

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-medium text-zinc-600 dark:text-zinc-400">
        {label}
      </div>
      {children}
    </label>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
      <div className="text-xs text-zinc-500">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
    </div>
  );
}
